/**
 * Выделенная field-поверхность «Статусы работ» (задача 122 §1.1) — ветка
 * «Статусы работ» хаба `/field/lead`, роут `/field/lead/work-status`.
 *
 * Аудитория экрана — field lead с телефона (та же, что у M6-подтверждения),
 * поэтому статусы работ живут в PWA-рамке хаба, а не в width-рефлоу офисного
 * `AppShell` (разворот классификации 114 §3.1; критерий выбора — `patterns.md
 * §Ширина против устройства`). Тело реестра переиспользуется без форка: тот же
 * `MobileRegistry` + `WorkStatusToolbar` + `useWorkRegistryHost`, что у
 * десктопной таблицы (`WorkStatusPage`), и та же `WorkPanel surface="mobile"`.
 *
 * Свой mobile-shell по образцу `LeadApprovalPage` (не worker-`FieldShell`: у
 * того logout через worker-эндпоинт, а у мастера office-сессия). Навигация
 * список → карточка → журнал — локальным состоянием, отдельных роутов нет
 * (паттерн «одно тело — два контейнера»).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";

import { UnitPicker } from "@/components/approval/UnitPicker";
import { WorkPanel } from "@/components/work-status/WorkPanel";
import {
  MobileRegistry,
  useWorkRegistryHost,
  WorkStatusToolbar,
} from "@/components/work-status/registry";
import { useUnits } from "@/hooks/useUnits";
import { useSession } from "@/store/session";
import type { WorkPair } from "@/api/workStatus";

export function FieldWorkStatusPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // admin гейтится ролью: его `acting_unit_ids` пуст, а реестр без юнита бэкенд
  // отвергает 422 — поэтому admin выбирает подразделение пикером (прецедент на
  // мобиле — консоль M6). Юнит и выбранная работа — локальный state: это
  // селектор сцены и навигация, URL-адресации на field-поверхности нет.
  const role = useSession((s) => s.user?.role);
  const isAdmin = role === "admin";
  const [unitId, setUnitId] = useState<number | null>(null);
  const [selected, setSelected] = useState<WorkPair | null>(null);
  const host = useWorkRegistryHost(unitId, false, role);
  const { data: units } = useUnits();

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-panel-1)]">
      <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-[var(--rule-line)] bg-[var(--bg-panel-0)] px-3">
        <button
          type="button"
          data-testid="field-work-status-back"
          aria-label={t("leadHub.back")}
          onClick={() => void navigate({ to: "/field/lead" })}
          className="inline-flex items-center gap-0.5 text-sm text-[var(--fg-2)]"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          {t("leadHub.back")}
        </button>
        <span className="flex-1" />
        <span className="text-sm font-semibold text-[var(--fg-1)]">{t("workStatus.title")}</span>
      </header>

      <main className="flex-1 p-3" data-testid="field-work-status">
        {/* Выбор подразделения — это выбор, ЧЕЙ реестр смотрим (только admin;
            у лида сцена — его поддерево). */}
        {isAdmin && <UnitPicker units={units ?? []} selectedUnitId={unitId} onChange={setUnitId} />}
        <WorkStatusToolbar
          query={host.query}
          onQueryChange={host.setQuery}
          statuses={host.statuses}
          onToggleStatus={host.toggleStatus}
          onReset={host.resetStatuses}
          isDefault={host.isDefault}
        />
        <MobileRegistry
          groups={host.groups}
          state={host.state}
          selected={selected}
          onSelect={setSelected}
          locale={i18n.language}
          error={host.error}
        />
      </main>

      {/* Карточка работы — fullscreen поверх реестра (`fixed inset-0` в панели);
          «назад» панели снимает выбор и возвращает к списку. */}
      {selected != null && (
        <WorkPanel
          pair={selected}
          surface="mobile"
          unitId={unitId}
          locale={i18n.language}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
